/**
 * GlobalSearchBar — site-wide cmd+K style search.
 *
 * Click-to-open OR ⌘K / Ctrl+K shortcut. Renders a centered modal with an
 * input and live-typing results (debounced 200ms) grouped by category.
 * Click a result → navigate + close. Click backdrop / press Esc → close.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, Users, Briefcase, TrendingUp, Gift, Calendar, Loader2,
  Landmark, Wrench, BookOpen,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_META = {
  members:      { icon: Users, label: 'Members', color: 'text-cyan-400' },
  businesses:   { icon: Briefcase, label: 'Businesses', color: 'text-emerald-400' },
  offers:       { icon: TrendingUp, label: 'Investor Offers', color: 'text-amber-400' },
  benefits:     { icon: Gift, label: 'Benefits', color: 'text-pink-400' },
  events:       { icon: Calendar, label: 'Events', color: 'text-purple-400' },
  gov_benefits: { icon: Landmark, label: 'Govt Schemes', color: 'text-blue-400' },
  services:     { icon: Wrench, label: 'Services', color: 'text-orange-400' },
  blog_posts:   { icon: BookOpen, label: 'Articles', color: 'text-teal-400' },
};

const GlobalSearchBar = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else {
      setQuery('');
      setResults(null);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/search`, { params: { q: query } });
        setResults(r.data);
      } catch { setResults(null); }
      finally { setLoading(false); }
    }, 200);
  }, [query]);

  const go = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="open-global-search"
        aria-label="Search site (Ctrl+K)"
        className="text-slate-300 hover:text-[#d4af37] transition-colors p-1.5 inline-flex items-center gap-1.5"
      >
        <Search size={18} />
        <span className="hidden lg:inline text-[11px] text-slate-500 border border-slate-700 px-1 py-0.5 rounded">⌘K</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm flex items-start justify-center pt-20 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="global-search-modal"
        >
          <div className="w-full max-w-2xl bg-slate-900 border border-[#d4af37]/30 max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <Search className="text-slate-500" size={18} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search members, businesses, offers, benefits, events…"
                data-testid="global-search-input"
                className="flex-1 bg-transparent text-white outline-none placeholder:text-slate-600"
              />
              {loading && <Loader2 className="text-slate-500 animate-spin" size={16} />}
              <button
                onClick={() => setOpen(false)}
                aria-label="Close search"
                data-testid="close-global-search"
                className="text-slate-500 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {!query || query.length < 2 ? (
                <div className="px-5 py-10 text-center text-sm text-slate-500">
                  Type at least 2 characters to search…
                  <div className="mt-3 flex justify-center gap-1.5 flex-wrap">
                    {Object.entries(CATEGORY_META).map(([k, m]) => (
                      <span key={k} className="text-[10px] uppercase tracking-wider bg-slate-800 text-slate-400 px-2 py-1 flex items-center gap-1">
                        <m.icon size={10} className={m.color} /> {m.label}
                      </span>
                    ))}
                  </div>
                </div>
              ) : !results || results.total === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-slate-500" data-testid="global-search-empty">
                  No results for &ldquo;{query}&rdquo;
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {Object.keys(CATEGORY_META).map((k) => {
                    const items = results[k] || [];
                    if (items.length === 0) return null;
                    const meta = CATEGORY_META[k];
                    const Icon = meta.icon;
                    return (
                      <div key={k} data-testid={`search-section-${k}`}>
                        <div className="px-5 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5 bg-slate-900">
                          <Icon size={11} className={meta.color} /> {meta.label} · {items.length}
                        </div>
                        {items.map((it) => (
                          <button
                            key={it.id}
                            onClick={() => go(it.path)}
                            data-testid={`search-result-${it.kind}-${it.id}`}
                            className="w-full text-left px-5 py-2.5 hover:bg-slate-800/60 flex items-center gap-3 transition-colors"
                          >
                            <Icon size={14} className={`${meta.color} flex-shrink-0`} />
                            <div className="min-w-0 flex-1">
                              <p className="text-white text-sm font-semibold truncate">{it.title}</p>
                              {it.subtitle && (
                                <p className="text-xs text-slate-500 truncate">{it.subtitle}</p>
                              )}
                            </div>
                            <span className="text-[10px] text-slate-600 flex-shrink-0">↵</span>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border-t border-white/10 px-5 py-2 text-[10px] text-slate-600 flex items-center justify-between bg-slate-900">
              <span>Press <kbd className="bg-slate-800 px-1.5 py-0.5">↵</kbd> to open</span>
              <span>Esc to close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearchBar;
